/**
 * useDeviceRegistration Hook
 *
 * Handles browser push device registration after user login.
 * Encapsulates the complete flow:
 * 1. Request notification permission
 * 2. Initialize service worker
 * 3. Subscribe to push notifications
 * 4. Gather device information
 * 5. Register device with backend
 * 6. Update local state
 *
 * Design: Non-blocking, silent failures, idempotent
 */

import { useCallback, useRef } from 'react';
import { useHttpClient, useAuth, UserPushDeviceService } from '@actschurch/ccms-sdk';
import type { RegisterMyDeviceViewModel } from '@actschurch/ccms-sdk';
import { ENV } from '@/config/environment';
import {
  initPushSystem,
  requestNotificationPermission,
  subscribeToPush,
  getSubscriptionInfo,
  getOrCreateDeviceId,
  updatePushLocalState,
  getPushLocalState,
  isNotificationSupported,
  isServiceWorkerSupported,
} from '@/utils/push';
import {
  getDeviceType,
  getDeviceName,
  getOSVersion,
  getTimezone,
  getPreferredLanguage,
  getAppVersion,
  getPlatform,
} from '@/utils/device';

interface DeviceRegistrationState {
  isRegistering: boolean;
  isRegistered: boolean;
  error: string | null;
}

export function useDeviceRegistration() {
  const httpClient = useHttpClient();
  const { state: authState } = useAuth();
  const registrationAttempted = useRef(false);

  /**
   * Register browser as push device
   * Safe to call multiple times - idempotent
   */
  const registerDevice = useCallback(async (): Promise<DeviceRegistrationState> => {
    const result: DeviceRegistrationState = {
      isRegistering: true,
      isRegistered: false,
      error: null,
    };

    try {
      // STEP 1: Validate prerequisites
      if (!authState.isAuthenticated || !authState.user) {
        console.warn('[DeviceRegistration] User not authenticated, skipping');
        return { ...result, isRegistering: false, error: 'Not authenticated' };
      }

      // Check if browser supports notifications
      if (!isNotificationSupported()) {
        console.warn('[DeviceRegistration] Notifications not supported, skipping');
        return { ...result, isRegistering: false, error: 'Notifications not supported' };
      }

      // Check if service worker supported
      if (!isServiceWorkerSupported()) {
        console.warn('[DeviceRegistration] Service Worker not supported, skipping');
        return { ...result, isRegistering: false, error: 'Service Worker not supported' };
      }

      // Check if VAPID key configured
      if (!ENV.VAPID_PUBLIC_KEY) {
        console.warn('[DeviceRegistration] VAPID public key not configured, skipping');
        return { ...result, isRegistering: false, error: 'VAPID key not configured' };
      }

      // STEP 2: Check if already registered (skip if registered within last 24 hours)
      const localState = getPushLocalState();
      const now = Date.now();
      const oneDayMs = 24 * 60 * 60 * 1000;

      if (
        localState.pushRegistered &&
        localState.pushLastUpdated &&
        now - localState.pushLastUpdated < oneDayMs
      ) {
        console.log('[DeviceRegistration] Device already registered recently, skipping');
        return { ...result, isRegistering: false, isRegistered: true };
      }

      // STEP 3: Initialize push system (service worker)
      console.log('[DeviceRegistration] Initializing push system...');
      await initPushSystem();

      // STEP 4: Request notification permission
      console.log('[DeviceRegistration] Requesting notification permission...');
      const permission = await requestNotificationPermission();

      if (permission !== 'granted') {
        console.warn('[DeviceRegistration] Notification permission denied');
        updatePushLocalState({
          pushRegistered: false,
          pushRetryCount: localState.pushRetryCount + 1,
        });
        return { ...result, isRegistering: false, error: 'Permission denied' };
      }

      // STEP 5: Subscribe to push notifications
      console.log('[DeviceRegistration] Subscribing to push...');
      const subscription = await subscribeToPush(ENV.VAPID_PUBLIC_KEY);

      if (!subscription) {
        console.error('[DeviceRegistration] Failed to subscribe to push');
        updatePushLocalState({
          pushRegistered: false,
          pushRetryCount: localState.pushRetryCount + 1,
        });
        return { ...result, isRegistering: false, error: 'Subscription failed' };
      }

      // STEP 6: Extract subscription info
      const subInfo = getSubscriptionInfo(subscription);
      const pushToken = JSON.stringify(subInfo); // Store full subscription as token

      // STEP 7: Gather device information
      const deviceId = getOrCreateDeviceId();
      const deviceType = getDeviceType();
      const platform = getPlatform();
      const deviceName = getDeviceName();
      const osVersion = getOSVersion();
      const timezone = getTimezone();
      const language = getPreferredLanguage();
      const appVersion = getAppVersion();

      // STEP 8: Prepare registration payload (userId comes from token on server)
      const registrationData: RegisterMyDeviceViewModel = {
        deviceId,
        deviceType,
        platform,
        pushToken,
        deviceName,
        osVersion: osVersion ?? undefined,
        language,
        timeZone: timezone,
        appVersion,
      };

      // STEP 9: Register device with backend (uses /my endpoint - userId from token)
      console.log('[DeviceRegistration] Registering device with backend...', {
        deviceId,
        deviceType,
        platform,
      });
      const userPushDeviceService = new UserPushDeviceService(httpClient);
      await userPushDeviceService.registerMyDevice(registrationData);

      // STEP 10: Update local state
      updatePushLocalState({
        pushRegistered: true,
        pushToken,
        pushRetryCount: 0,
      });

      console.log('[DeviceRegistration] Device registered successfully!');
      return { isRegistering: false, isRegistered: true, error: null };
    } catch (error: any) {
      // Silent failure - log error but don't block user
      console.error('[DeviceRegistration] Registration failed:', error);

      const localState = getPushLocalState();
      updatePushLocalState({
        pushRegistered: false,
        pushRetryCount: localState.pushRetryCount + 1,
      });

      return {
        isRegistering: false,
        isRegistered: false,
        error: error.message || 'Unknown error',
      };
    }
  }, [httpClient, authState]);

  /**
   * Auto-register on login
   * Call this from onLogin callback
   * Non-blocking - runs in background
   */
  const autoRegisterOnLogin = useCallback(async () => {
    // Prevent double registration
    if (registrationAttempted.current) {
      console.log('[DeviceRegistration] Registration already attempted this session');
      return;
    }

    registrationAttempted.current = true;

    // Small delay to ensure auth state is settled
    setTimeout(() => {
      registerDevice().catch((error) => {
        console.error('[DeviceRegistration] Auto-registration failed:', error);
        // Silent failure - user can still use app
      });
    }, 1000);
  }, [registerDevice]);

  /**
   * Reset registration attempt flag
   * Call on logout to allow re-registration on next login
   */
  const resetRegistration = useCallback(() => {
    registrationAttempted.current = false;
  }, []);

  return {
    registerDevice,
    autoRegisterOnLogin,
    resetRegistration,
  };
}
